import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, LUMIO, SIDE_MENU_MORE } from '../../../../theme/tokens';

/** Fixed width of the flyout (must match the value `place()` anchors with). */
export const MENU_WIDTH = 300;

/**
 * The past-conversations flyout surface. Mirrors the side-menu `MoreMenu`: a
 * portal-to-<body>, `fixed`-positioned panel (left/top/max-height set inline from
 * the trigger's viewport rect) so it floats above the content card regardless of
 * stacking context and clamps to the viewport (the inner list scrolls). Reuses the
 * shared flyout tokens, so it is theme-aware (light/dark) with no extra work. It
 * opens DOWNWARD from the header "…" button, hence `transform-origin: top right`.
 */
export const Surface = styled.div<{ $open: boolean }>`
  position: fixed;
  z-index: 1000;

  width: ${MENU_WIDTH}px;
  max-width: calc(100vw - 16px);

  display: flex;
  flex-direction: column;
  overflow: hidden;

  border-radius: 20px;
  background: ${SIDE_MENU_MORE.bg};
  border: 1px solid ${SIDE_MENU_MORE.border};
  box-shadow: ${SIDE_MENU_MORE.shadow};

  transform-origin: top right;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'none' : 'translateY(-6px) scale(0.96)')};
  transition:
    opacity ${({ $open }) => ($open ? ANIM.MENU_OPEN_MS : ANIM.MENU_CLOSE_MS)}ms ${ANIM.EASE},
    transform ${({ $open }) => ($open ? ANIM.MENU_OPEN_MS : ANIM.MENU_CLOSE_MS)}ms ${ANIM.EASE};
  will-change: opacity, transform;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  &:focus {
    outline: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
    transform: none;
  }
`;

/** "Past conversations" caption, pinned above the scrolling list. */
export const Heading = styled(Text).attrs({ tag: 'div' as const })`
  flex: 0 0 auto;
  padding: 16px 16px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${LUMIO.action};
`;

export const Scroll = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 0 8px 8px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/** One past conversation: a two-line button (title + relative time). */
export const Item = styled.button<{ $index: number; $open: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'none' : 'translateY(4px)')};
  transition:
    opacity ${ANIM.MENU_OPEN_MS}ms ${ANIM.EASE},
    transform ${ANIM.MENU_OPEN_MS}ms ${ANIM.EASE},
    background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  transition-delay: ${({ $open, $index }) => ($open ? $index * ANIM.MENU_ROW_STAGGER_MS : 0)}ms;

  &:hover,
  &:focus-visible {
    background: ${SIDE_MENU_MORE.rowHover};
    outline: none;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: background-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;

/** Title = the conversation's first message, clamped to two lines. */
export const ItemTitle = styled(Text).attrs({ tag: 'span' as const })`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: ${SIDE_MENU_MORE.label};
`;

/** Relative time of the last message (already-localised string, rendered as-is). */
export const ItemTime = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: ${LUMIO.action};
`;

/** Loading / empty / error states share this padded column. */
export const StateRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  padding: 4px 12px 12px;
`;

export const StateText = styled(Text).attrs({ tag: 'div' as const })`
  font-size: 13px;
  color: ${LUMIO.action};
`;

/** A single loading row: a title line + a shorter time line. */
export const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
`;
